/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { dsAppUidProp } from 'componentregistry/components/DataStore/DataStoreManager';
import { importFileType } from 'componentregistry/registry/constants';
import { isEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionImport';

const ActionImportManager: ActionManagerFn<'ActionImport'> = (
  platform: Platform,
): ActionManager<'ActionImport'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionImport'),
    isChildAllowed: (page, item, childType) =>
      ['ActionOnResponse'].includes(childType),
    defaultValue,
    props: {
      ...baseActionProps,
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Data Source',
        type: 'DataSourceChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
      },
      gridItemId: {
        label: 'Grid Item ID',
        type: 'ItemID',
      },
      fileType: {
        label: 'File Type',
        options: importFileType,
        type: 'select',
      },
      hideInsertMode: {
        label: 'Hide Insert Mode',
        type: 'boolean',
      },
      hideUpdateMode: {
        label: 'Hide Update Mode',
        type: 'boolean',
      },
      mergeMode: {
        label: ' Merge Mode',
        type: 'boolean',
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'dsAppUid') {
        callback();
        return {
          [property]: value,
          dataSource: '',
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'hideInsertMode':
          return !props.mergeMode && !props.hideUpdateMode;
        case 'hideUpdateMode':
          return !props.mergeMode && !props.hideInsertMode;
        case 'mergeMode':
          return !props.hideInsertMode && !props.hideUpdateMode;
        case 'dataSource':
          return isEmpty(props.gridItemId);
        case 'gridItemId':
          return isEmpty(props.dataSource);
        default:
          return true;
      }
    },
    name: 'Import Data',
    icon: { iconName: 'file-import' },
    getDisplayText: (item) => item.props.dataSource,
    description: `Use this action to import data into the table using csv/xlsx file.\n
    
      Use \`On Response\` child action to perform further actions after import.

\`\`\`javascript
// The info related to import is available as $._.response
// Sample expression using the above values from the import
\`\${$._.response.fileName} \${$._.response.count} \${$._.response.uploadMode}!\`
\`\`\`

`,
  };
};

export default ActionImportManager;
